import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CloseIcon from "@mui/icons-material/Close";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import type { GridColDef } from "@mui/x-data-grid";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import { LojaDataGrid } from "layouts/pages/loja/common";
import { setMessage, useMaterialUIController } from "context";

import FormVincularPatrocinador from "./FormVincularPatrocinador";
import {
  PATROCINADORES_ACCENT,
  patrocinadoresModalContentSx,
  patrocinadoresModalHeaderSx,
  patrocinadoresModalPaperSx,
  patrocinadoresToolbarSx,
} from "./patrocinadores-evento.theme";

import { IEventoResponse } from "interfaces/eventos";
import { IPatrocinador } from "interfaces/patrocinador";
import {
  desvincularPatrocinadorEvento,
  getAllPatrocinadores,
} from "services/patrocinadores";

interface Props {
  evento: IEventoResponse;
  updateEvento: () => void;
}

export type PatrocinadoresEventoRow = {
  id: number;
  patrocinadorId: number;
  ordem: number;
  patrocinador: IPatrocinador;
};

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h5"
        fontWeight="bold"
        color={accent ? PATROCINADORES_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function mapPatrocinadoresEvento(evento: IEventoResponse): PatrocinadoresEventoRow[] {
  const lista = (evento as IEventoResponse & { patrocinadores?: unknown[] })
    .patrocinadores;

  if (!Array.isArray(lista)) return [];

  return lista.map((pe: unknown) => {
    const item = pe as {
      patrocinadorId?: number;
      ordem?: number;
      patrocinador?: IPatrocinador;
    };
    const patrocinadorId = item.patrocinadorId ?? item.patrocinador?.id ?? 0;

    return {
      id: patrocinadorId,
      patrocinadorId,
      ordem: item.ordem ?? 0,
      patrocinador: item.patrocinador as IPatrocinador,
    };
  });
}

export default function PatrocinadoresEvento({ evento, updateEvento }: Props) {
  const [, dispatch] = useMaterialUIController();
  const [allSponsors, setAllSponsors] = useState<IPatrocinador[]>([]);
  const [loadingSponsors, setLoadingSponsors] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [toUnlink, setToUnlink] = useState<PatrocinadoresEventoRow | null>(null);

  const linked = useMemo(() => mapPatrocinadoresEvento(evento), [evento]);

  const loadSponsors = useCallback(() => {
    setLoadingSponsors(true);
    getAllPatrocinadores()
      .then((data) => setAllSponsors(Array.isArray(data) ? data : []))
      .catch((error: unknown) => {
        setAllSponsors([]);
        setMessage(dispatch, {
          open: true,
          text:
            error instanceof Error
              ? error.message
              : "Erro ao carregar patrocinadores",
          severity: "error",
        });
      })
      .finally(() => setLoadingSponsors(false));
  }, [dispatch]);

  useEffect(() => {
    loadSponsors();
  }, [loadSponsors]);

  const alreadyLinkedIds = useMemo(
    () => new Set(linked.map((row) => row.patrocinadorId)),
    [linked],
  );

  const availableOptions = useMemo(
    () => allSponsors.filter((s) => s.ativo && !alreadyLinkedIds.has(s.id)),
    [allSponsors, alreadyLinkedIds],
  );

  const stats = useMemo(() => {
    const comSite = linked.filter((row) => row.patrocinador?.urlSite?.trim())
      .length;
    return {
      vinculados: linked.length,
      disponiveis: availableOptions.length,
      comSite,
    };
  }, [linked, availableOptions.length]);

  const handleConfirmDesvincular = async () => {
    if (!toUnlink) return;

    try {
      await desvincularPatrocinadorEvento(toUnlink.patrocinadorId, evento.id);
      setMessage(dispatch, {
        open: true,
        text: "Patrocinador desvinculado do evento",
        severity: "success",
      });
      setToUnlink(null);
      updateEvento();
    } catch {
      setMessage(dispatch, {
        open: true,
        text: "Erro ao remover vínculo",
        severity: "error",
      });
    }
  };

  const columns: GridColDef<PatrocinadoresEventoRow>[] = [
    {
      field: "logo",
      headerName: "Logo",
      width: 88,
      sortable: false,
      renderCell: (params) =>
        params.row.patrocinador?.urlLogo ? (
          <Box
            component="img"
            src={params.row.patrocinador.urlLogo}
            alt={params.row.patrocinador.nome}
            sx={{
              maxHeight: 40,
              maxWidth: 72,
              objectFit: "contain",
            }}
          />
        ) : (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        ),
    },
    {
      field: "nome",
      headerName: "Patrocinador",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Stack spacing={0.25} minWidth={0}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {params.row.patrocinador?.nome ?? "—"}
          </Typography>
          {params.row.patrocinador?.descricao ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {params.row.patrocinador.descricao}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      field: "ordem",
      headerName: "Ordem",
      width: 90,
      renderCell: (params) => (
        <Chip size="small" label={params.row.ordem} variant="outlined" />
      ),
    },
    {
      field: "urlSite",
      headerName: "Site",
      width: 72,
      sortable: false,
      renderCell: (params) =>
        params.row.patrocinador?.urlSite ? (
          <Tooltip title="Abrir site">
            <IconButton
              component="a"
              href={params.row.patrocinador.urlSite}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              color="info"
            >
              <OpenInNewOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        ),
    },
    {
      field: "acoes",
      headerName: "",
      width: 52,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Desvincular">
          <IconButton
            size="small"
            color="error"
            onClick={() => setToUnlink(params.row)}
          >
            <LinkOffOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <MDBox>
      <MDDialog
        title="Desvincular patrocinador"
        message={`Remover "${toUnlink?.patrocinador?.nome}" deste evento?`}
        open={!!toUnlink}
        onClose={() => setToUnlink(null)}
        response={(resp: boolean) => {
          if (resp) void handleConfirmDesvincular();
          else setToUnlink(null);
        }}
      />

      <Box sx={patrocinadoresToolbarSx}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <HandshakeOutlinedIcon sx={{ color: PATROCINADORES_ACCENT }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Patrocinadores do evento
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {evento.titulo}
              </Typography>
            </Box>
          </Stack>
          <MDButton
            variant="gradient"
            color="dark"
            onClick={() => setFormOpen(true)}
            startIcon={<AddOutlinedIcon />}
            disabled={availableOptions.length === 0}
            sx={{
              flexShrink: 0,
              background: `linear-gradient(195deg, ${PATROCINADORES_ACCENT}, #e04a1a)`,
            }}
          >
            Vincular patrocinador
          </MDButton>
        </Stack>
      </Box>

      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={6} sm={4}>
          <StatCard label="Vinculados" value={stats.vinculados} accent />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard label="Disponíveis para vincular" value={stats.disponiveis} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Com site configurado" value={stats.comSite} />
        </Grid>
      </Grid>

      {linked.length === 0 ? (
        <Box
          sx={{
            py: 6,
            px: 2,
            textAlign: "center",
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "grey.300",
            bgcolor: "grey.50",
          }}
        >
          <HandshakeOutlinedIcon
            sx={{ fontSize: 44, color: "grey.400", mb: 1 }}
          />
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Nenhum patrocinador vinculado
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Vincule patrocinadores cadastrados para exibir neste evento.
          </Typography>
          <MDButton
            variant="outlined"
            color="dark"
            onClick={() => setFormOpen(true)}
            startIcon={<AddOutlinedIcon />}
            disabled={availableOptions.length === 0}
          >
            Vincular primeiro patrocinador
          </MDButton>
        </Box>
      ) : (
        <LojaDataGrid
          rows={linked}
          columns={columns}
          getRowId={(row) => String(row.id)}
          autoHeight
          disableColumnMenu
          disableRowSelectionOnClick
          rowHeight={64}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          pageSizeOptions={[5, 10, 25]}
          localeText={{ noRowsLabel: "Nenhum patrocinador vinculado" }}
          sx={{
            "& .MuiDataGrid-row:hover": {
              bgcolor: "rgba(249, 96, 48, 0.04)",
            },
          }}
        />
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: patrocinadoresModalPaperSx }}
      >
        <Box sx={patrocinadoresModalHeaderSx}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              component="img"
              src={evento.urlImageDestaque}
              alt={evento.titulo}
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1.5,
                objectFit: "cover",
                border: "2px solid rgba(255,255,255,0.2)",
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="overline"
                sx={{ opacity: 0.75, letterSpacing: 1 }}
              >
                Vincular patrocinador
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {evento.titulo}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setFormOpen(false)}
              sx={{ color: "inherit" }}
              aria-label="Fechar"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent sx={patrocinadoresModalContentSx}>
          <FormVincularPatrocinador
            eventoId={evento.id}
            options={availableOptions}
            handleClose={() => setFormOpen(false)}
            updateEvento={updateEvento}
            onRefreshOptions={loadSponsors}
            refreshingOptions={loadingSponsors}
          />
        </DialogContent>
      </Dialog>
    </MDBox>
  );
}
